/*
 * Super Admin — Onboarding & Purchase Orders (Phase D).
 * Two tabs: Purchase Orders (review/accept/reject, invoice, mark-paid, download PO)
 * and Vendor Package config editor.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileText, Check, X, DollarSign, Download, RefreshCw, Save, Package, KeyRound, Copy, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;
const buildHeaders = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id": session?.user?.id || "admin",
  "x-user-role": session?.user?.role || "admin",
});
const fmt = (n) => "$" + Number(n || 0).toLocaleString();
const STATUS_COLOR = {
  under_review: "bg-amber-100 text-amber-800", accepted: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800", paid: "bg-blue-100 text-blue-800",
};

export default function OnboardingAdminPage({ session }) {
  const headers = useMemo(() => buildHeaders(session), [session]);
  const [tab, setTab] = useState("pos");
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState(null);
  const [busy, setBusy] = useState("");
  const [credEmail, setCredEmail] = useState("");
  const [credResult, setCredResult] = useState(null);
  const [credCopied, setCredCopied] = useState(false);
  const [notif, setNotif] = useState(null);
  const [notifLabels, setNotifLabels] = useState({});
  const [savingNotif, setSavingNotif] = useState(false);
  const [ccInput, setCcInput] = useState("");

  const loadNotif = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/onboarding/notification-settings`, { headers });
      setNotif(r.data.settings); setNotifLabels(r.data.event_labels || {});
    } catch { /* no-op */ }
  }, [headers]);
  const saveNotif = async () => {
    setSavingNotif(true);
    try {
      const cc = ccInput.split(",").map((e) => e.trim()).filter(Boolean);
      const r = await axios.put(`${API}/api/admin/onboarding/notification-settings`,
        { primary_email: notif.primary_email, cc_emails: cc, events: notif.events }, { headers });
      setNotif(r.data.settings); setCcInput((r.data.settings.cc_emails || []).join(", "));
      toast.success("Notification settings saved.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); } finally { setSavingNotif(false); }
  };

  const loadPos = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/onboarding/purchase-orders`, { headers });
      setPos(r.data.purchase_orders || []);
    } catch { toast.error("Failed to load purchase orders"); } finally { setLoading(false); }
  }, [headers]);
  const loadVendor = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/admin/onboarding/vendor-package`, { headers }); setVendor(r.data); } catch {}
  }, [headers]);
  useEffect(() => { loadPos(); loadVendor(); loadNotif(); }, [loadPos, loadVendor, loadNotif]);

  const review = async (po, decision) => {
    const notes = decision === "reject" ? (window.prompt("Rejection reason (optional):") || "") : "";
    setBusy(po.id);
    try {
      await axios.post(`${API}/api/admin/onboarding/purchase-orders/${po.id}/review`, { decision, review_notes: notes }, { headers });
      toast.success(decision === "accept" ? "PO accepted — tenant provisioned." : "PO rejected.");
      loadPos();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); } finally { setBusy(""); }
  };
  const sendInvoice = async (po) => {
    setBusy(po.id);
    try { await axios.post(`${API}/api/admin/onboarding/purchase-orders/${po.id}/invoice`, {}, { headers }); toast.success("Invoice marked sent."); loadPos(); }
    catch { toast.error("Failed"); } finally { setBusy(""); }
  };
  const markPaid = async (po) => {
    setBusy(po.id);
    try { await axios.post(`${API}/api/admin/onboarding/purchase-orders/${po.id}/mark-paid`, {}, { headers }); toast.success("Marked paid — license active."); loadPos(); }
    catch { toast.error("Failed"); } finally { setBusy(""); }
  };
  const downloadPo = async (po) => {
    try {
      const r = await axios.get(`${API}/api/admin/onboarding/po/${po.po_file_id}/download`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch { toast.error("Download failed"); }
  };
  const saveVendor = async () => {
    setBusy("vendor");
    try { const r = await axios.put(`${API}/api/admin/onboarding/vendor-package`, vendor, { headers }); setVendor(r.data); toast.success("Vendor package saved."); }
    catch { toast.error("Save failed"); } finally { setBusy(""); }
  };

  const lookupCred = async (reset) => {
    if (!credEmail.trim()) { toast.error("Enter the customer's email"); return; }
    setBusy("cred");
    try {
      const r = await axios.post(`${API}/api/admin/onboarding/credentials`, { email: credEmail.trim(), reset: !!reset }, { headers });
      setCredResult(r.data);
      if (reset) toast.success("New temporary password issued.");
    } catch (err) { setCredResult(null); toast.error(err?.response?.data?.detail || "Lookup failed"); } finally { setBusy(""); }
  };
  const copyCred = () => { if (credResult?.temp_password) { navigator.clipboard.writeText(credResult.temp_password); setCredCopied(true); setTimeout(() => setCredCopied(false), 2000); } };
  useEffect(() => { if (notif && ccInput === "") setCcInput((notif.cc_emails || []).join(", ")); }, [notif]); // eslint-disable-line

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6" data-testid="onboarding-admin">
      <div className="mb-5">
        <div className="text-[11.5px] font-semibold tracking-[0.14em] uppercase text-[#9aa2b1]">Super Admin · Onboarding</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#111827]">Purchase Orders & Vendor Setup</h1>
        <p className="text-sm text-[#6b7280] mt-1">Review purchase orders, issue invoices, activate licenses, and manage the vendor package.</p>
      </div>

      <div className="flex gap-2 border-b border-[#e5e7eb] mb-5">
        {[["pos", "Purchase Orders", FileText], ["vendor", "Vendor Package", Package], ["credentials", "Login Credentials", KeyRound], ["notifications", "Email Alerts", Bell]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-[13px] font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === k ? "border-[#007a9e] text-[#111827]" : "border-transparent text-[#6b7280]"}`} data-testid={`onboarding-tab-${k}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
        <Button variant="ghost" size="sm" onClick={() => { loadPos(); loadVendor(); }} className="ml-auto h-8"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {tab === "pos" && (
        <div data-testid="onboarding-po-list">
          {loading ? <div className="py-10 text-center text-[#6b7280]"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
          : pos.length === 0 ? <div className="py-10 text-center text-[#6b7280]">No purchase orders yet.</div>
          : (
            <div className="space-y-3">
              {pos.map((po) => (
                <div key={po.id} className="rounded-xl border border-[#e5e7eb] bg-white p-4" data-testid={`po-card-${po.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-bold text-[#111827]">{po.quote?.school_name || po.quote?.authorizer_org_name || "—"}</span>
                        {po.quote?.purchaser_type === "authorizer" && <span className="text-[11.5px] font-bold uppercase px-2 py-0.5 rounded bg-indigo-100 text-indigo-800">Portfolio</span>}
                        <span className={`text-[11.5px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_COLOR[po.status] || "bg-gray-100 text-gray-700"}`}>{(po.status || "").replace("_", " ")}</span>
                        {po.payment_status === "paid" && <span className="text-[11.5px] font-bold uppercase px-2 py-0.5 rounded bg-blue-100 text-blue-800">Paid</span>}
                      </div>
                      <div className="text-[12px] text-[#6b7280] mt-0.5">Quote {po.quote?.quote_number} · {fmt(po.quote?.annual_total)}{po.quote?.purchaser_type === "authorizer" ? ` · ${po.quote?.selected_school_count || 0} school(s)` : ""} · PO {po.po_number || "(none)"} · from {po.submitted_by_email || "—"}</div>
                      {po.invoice_number && <div className="text-[12px] text-[#374151] mt-0.5">Invoice: {po.invoice_number}</div>}
                      {po.review_notes && <div className="text-[12px] text-[#b91c1c] mt-0.5">Note: {po.review_notes}</div>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {po.po_file_id && <Button size="sm" variant="outline" onClick={() => downloadPo(po)} data-testid={`po-download-${po.id}`}><Download className="h-3.5 w-3.5 mr-1" />PO</Button>}
                      {po.status === "under_review" && (
                        <>
                          <Button size="sm" disabled={busy === po.id} onClick={() => review(po, "accept")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`po-accept-${po.id}`}><Check className="h-3.5 w-3.5 mr-1" />Accept</Button>
                          <Button size="sm" disabled={busy === po.id} variant="outline" onClick={() => review(po, "reject")} className="text-red-600 border-red-200" data-testid={`po-reject-${po.id}`}><X className="h-3.5 w-3.5 mr-1" />Reject</Button>
                        </>
                      )}
                      {po.status === "accepted" && !po.invoice_number && <Button size="sm" disabled={busy === po.id} onClick={() => sendInvoice(po)} data-testid={`po-invoice-${po.id}`}><FileText className="h-3.5 w-3.5 mr-1" />Send Invoice</Button>}
                      {po.status === "accepted" && po.payment_status !== "paid" && <Button size="sm" disabled={busy === po.id} onClick={() => markPaid(po)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`po-mark-paid-${po.id}`}><DollarSign className="h-3.5 w-3.5 mr-1" />Mark Paid</Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "vendor" && vendor && (
        <div className="max-w-2xl rounded-xl border border-[#e5e7eb] bg-white p-6 space-y-4" data-testid="onboarding-vendor-editor">
          {[["vendor_legal_name", "Vendor Legal Name"], ["vendor_tax_id", "Tax ID"], ["remittance_email", "Remittance Email"], ["payment_terms", "Payment Terms"]].map(([k, label]) => (
            <div key={k}>
              <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">{label}</label>
              <Input value={vendor[k] || ""} onChange={(e) => setVendor((v) => ({ ...v, [k]: e.target.value }))} className="h-10" data-testid={`vendor-${k}`} />
            </div>
          ))}
          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Instructions</label>
            <Textarea value={vendor.instructions || ""} onChange={(e) => setVendor((v) => ({ ...v, instructions: e.target.value }))} className="min-h-[80px]" data-testid="vendor-instructions" />
          </div>
          <Button disabled={busy === "vendor"} onClick={saveVendor} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="vendor-save-btn">
            {busy === "vendor" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Vendor Package
          </Button>
        </div>
      )}

      {tab === "credentials" && (
        <div className="max-w-xl rounded-xl border border-[#e5e7eb] bg-white p-6 space-y-4" data-testid="onboarding-credentials">
          <div>
            <h3 className="text-[15px] font-bold text-[#111827]">Retrieve or reset a customer's login</h3>
            <p className="text-[13px] text-[#6b7280] mt-1">Use this when the welcome email couldn't be delivered. Look up by the email the customer signed up with.</p>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Customer email</label>
            <Input value={credEmail} onChange={(e) => setCredEmail(e.target.value)} placeholder="name@school.org" className="h-10" data-testid="cred-email" />
          </div>
          <div className="flex gap-2">
            <Button disabled={busy === "cred"} onClick={() => lookupCred(false)} variant="outline" data-testid="cred-lookup-btn">
              {busy === "cred" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Look up
            </Button>
            <Button disabled={busy === "cred"} onClick={() => lookupCred(true)} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="cred-reset-btn">
              <KeyRound className="h-4 w-4 mr-2" />Reset password
            </Button>
          </div>

          {credResult && (
            <div className="rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-4" data-testid="cred-result">
              {credResult.provisioned === false ? (
                <div className="text-[13px] text-[#92400e]">
                  <div className="font-semibold">{credResult.school_name || "This signup"} is not provisioned yet.</div>
                  <div className="mt-1">{credResult.message}</div>
                </div>
              ) : (
                <div className="text-[13px] text-[#374151] space-y-1">
                  <div><span className="font-semibold">School:</span> {credResult.school_name || "—"}</div>
                  <div><span className="font-semibold">Login email:</span> {credResult.login_email}</div>
                  <div><span className="font-semibold">Login URL:</span> {credResult.login_url}</div>
                  {credResult.temp_password ? (
                    <div className="flex items-center gap-2 pt-1">
                      <span className="font-semibold">Temp password:</span>
                      <code className="px-2 py-0.5 rounded bg-white border border-[#e5e7eb] text-[13px]" data-testid="cred-password">{credResult.temp_password}</code>
                      <Button size="sm" variant="outline" className="h-7" onClick={copyCred} data-testid="cred-copy-btn">{credCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}</Button>
                      {credResult.source === "reset" && <span className="text-[11.5px] text-[#059669]">New password issued</span>}
                    </div>
                  ) : (
                    <div className="pt-1 text-[#92400e]">{credResult.message || "Click Reset password to issue a new one."}</div>
                  )}
                  <p className="text-[11.5px] text-[#9ca3af] pt-1">Share this with the customer. They'll be asked to change it on first sign-in.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "notifications" && (
        <div data-testid="onboarding-notifications" className="max-w-2xl">
          <p className="text-[13px] text-[#6b7280] mb-4">Choose which events email the Super Admin and add CC recipients (e.g., a sales-ops inbox). Applies to new signups, purchase orders, and payments.</p>
          {!notif ? (
            <div className="text-[#6b7280] text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
          ) : (
            <div className="rounded-lg border border-[#e5e7eb] bg-white p-5 space-y-5">
              <div>
                <label className="text-[12px] font-semibold uppercase tracking-wide text-[#6b7280]">Primary recipient</label>
                <Input type="email" value={notif.primary_email || ""} onChange={(e) => setNotif((p) => ({ ...p, primary_email: e.target.value }))} className="mt-1 h-10" data-testid="notif-primary-email" />
              </div>
              <div>
                <label className="text-[12px] font-semibold uppercase tracking-wide text-[#6b7280]">CC recipients (comma-separated)</label>
                <Input value={ccInput} onChange={(e) => setCcInput(e.target.value)} placeholder="salesops@589labs.ai, ap@589labs.ai" className="mt-1 h-10" data-testid="notif-cc-emails" />
              </div>
              <div>
                <label className="text-[12px] font-semibold uppercase tracking-wide text-[#6b7280]">Events</label>
                <div className="mt-2 space-y-2">
                  {Object.entries(notifLabels).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-3 text-[14px] text-[#374151] cursor-pointer" data-testid={`notif-event-${key}`}>
                      <input type="checkbox" checked={!!notif.events?.[key]} onChange={(e) => setNotif((p) => ({ ...p, events: { ...p.events, [key]: e.target.checked } }))} className="h-4 w-4 accent-[#007a9e]" />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <Button onClick={saveNotif} disabled={savingNotif} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="notif-save-btn">
                {savingNotif ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : <><Save className="h-4 w-4 mr-2" />Save settings</>}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
